import type {
	WorkOrder,
	WorkOrderEvidence,
	WorkOrderQuestion,
	WorkOrderRecord,
	WorkOrderSession,
} from "@agentry/zerovia-contracts";
import type { WorkOrderFrameView, WorkOrderProgressView } from "../types.js";
import { PROJECT_MAIN, TENANT_MAIN } from "./seed.js";

const createdBy = "user-member-01";
const base = "2026-08-28T04:00:00.000Z";

function order(
	id: string,
	title: string,
	status: WorkOrder["status"],
	branch: string,
	overrides: Partial<WorkOrder> = {},
): WorkOrder {
	return {
		id,
		tenantId: TENANT_MAIN,
		projectId: PROJECT_MAIN,
		title,
		kind: "implementation",
		deployStage: null,
		targetRepo: "agentry",
		branch,
		specMarkdown: `# ${title}\n\n## 受け入れ条件\n\n- 主要導線をブラウザで確認できる\n- pnpm verify が成功する`,
		journeyScript: null,
		scheduledAt: null,
		journeyLlmCapSeconds: null,
		journeyFault: null,
		campaignId: null,
		campaignOrdinal: null,
		campaignSize: null,
		status,
		blockedFromStatus: status === "blocked" ? "implemented" : null,
		createdBy,
		attentionRequired: status === "blocked" || status === "verified",
		deletedAt: null,
		deletedBy: null,
		requiredSecrets: [],
		createdAt: base,
		updatedAt: base,
		...overrides,
	};
}

export const workOrders: WorkOrder[] = [
	order("wo-mock-blocked", "裁定待ち: 公開対象の最終確認", "blocked", "cs/041-publish-scope"),
	order("wo-mock-draft", "検索結果の空状態を整える", "draft", "cs/040-empty-search"),
	order("wo-mock-issued", "利用状況カードを追加", "issued", "cs/039-usage-card"),
	order("wo-mock-implemented", "ボードの工程ライブ表示", "implemented", "cs/026-board-ui"),
	order("wo-mock-verified", "ログイン導線の回帰確認", "verified", "cs/029-login-regression"),
	order("WO-E2E-1", "Persistent acceptance loop v2", "accepted", "work-order/wo-e2e-1", {
		updatedAt: "2026-08-28T06:20:00.000Z",
	}),
	order("wo-mock-rejected", "旧ナビゲーション案", "rejected", "cs/024-old-navigation"),
	order("wo-mock-deleted", "削除済みカードの復元確認", "draft", "cs/mock-restore", {
		deletedAt: "2026-08-28T05:00:00.000Z",
		deletedBy: createdBy,
	}),
];

export const questions: WorkOrderQuestion[] = [
	{
		id: "question-mock-1",
		orderId: "wo-mock-blocked",
		seq: 1,
		questionMarkdown: "公開対象は platform tenant 全体でよいですか？",
		status: "open",
		answerMarkdown: null,
		askedByActorType: "implement_agent",
		askedByActorId: "agent-implement-01",
		answeredByActorType: null,
		answeredByActorId: null,
		createdAt: "2026-08-28T05:15:00.000Z",
		answeredAt: null,
	},
];

export const records: WorkOrderRecord[] = [
	{
		id: "record-e2e-1",
		orderId: "WO-E2E-1",
		seq: 1,
		kind: "受け入れ",
		bodyMarkdown: "受け入れテストと stg 確認を完了しました。",
		actorType: "human",
		actorId: createdBy,
		createdAt: "2026-08-28T06:20:00.000Z",
	},
	{
		id: "record-verified-1",
		orderId: "wo-mock-verified",
		seq: 1,
		kind: "詳細確認",
		bodyMarkdown: "Mock / Real のログイン導線を再確認済みです。",
		actorType: "accept_agent",
		actorId: "agent-accept-01",
		createdAt: "2026-08-28T05:55:00.000Z",
	},
];

export const evidence: WorkOrderEvidence[] = [
	{
		id: "evidence-implemented-1",
		orderId: "wo-mock-implemented",
		seq: 1,
		kind: "ci_status",
		payload: { status: "running", url: "https://example.test/ci/026" },
		actorType: "implement_agent",
		actorId: "agent-implement-01",
		createdAt: "2026-08-28T05:40:00.000Z",
	},
	{
		id: "evidence-e2e-1",
		orderId: "WO-E2E-1",
		seq: 1,
		kind: "release",
		payload: { environment: "stg", url: "https://example.test/releases/wo-e2e-1" },
		actorType: "accept_agent",
		actorId: "agent-accept-01",
		createdAt: "2026-08-28T06:15:00.000Z",
	},
];

export const sessions: WorkOrderSession[] = [
	{
		id: "order-session-implement-026",
		orderId: "wo-mock-implemented",
		role: "implement",
		attempt: 2,
		agentSessionId: "agent-session-026",
		runtimeId: "runtime-026",
		status: "active",
		framesAvailable: true,
		createdAt: "2026-08-28T05:10:00.000Z",
		endedAt: null,
	},
	{
		id: "order-session-accept-029",
		orderId: "wo-mock-verified",
		role: "accept",
		attempt: 1,
		agentSessionId: "agent-session-029",
		runtimeId: "runtime-029",
		status: "succeeded",
		framesAvailable: true,
		createdAt: "2026-08-28T05:30:00.000Z",
		endedAt: "2026-08-28T05:55:00.000Z",
	},
];

export const frames: WorkOrderFrameView[] = [
	{
		id: "frame-design-1",
		orderId: null,
		sessionId: "project-design-session-01",
		sequence: 1,
		role: "assistant",
		body: "このボードで依頼の起票と進捗確認ができます。",
		createdAt: base,
	},
	{
		id: "frame-implement-1",
		orderId: "wo-mock-implemented",
		sessionId: "order-session-implement-026",
		sequence: 1,
		role: "assistant",
		body: "UI骨格を実装し、検証を進めています。",
		createdAt: "2026-08-28T05:35:00.000Z",
	},
];

export const progress: Record<string, WorkOrderProgressView> = {
	"wo-mock-issued": {
		sequence: 0,
		phase: "queued",
		label: "実装準備",
		startedAt: "2026-08-28T05:20:00.000Z",
		retryCount: 0,
		retryReason: null,
		nextRetryAt: null,
		live: true,
		llmActiveSeconds: 0,
		failureClass: null,
		warning: null,
		wallClock: emptyWallClock(),
		journey: null,
	},
	"wo-mock-implemented": {
		sequence: 1,
		phase: "verify",
		label: "ローカル検証中",
		startedAt: "2026-08-28T05:10:00.000Z",
		retryCount: 1,
		retryReason: "依存キャッシュを再構築して再試行",
		nextRetryAt: null,
		live: true,
		llmActiveSeconds: 138,
		failureClass: null,
		warning: "再試行後に復旧しました。",
		wallClock: { ...emptyWallClock(), totalSeconds: 420, llmSeconds: 138, ciSeconds: 210 },
		journey: null,
	},
	"WO-E2E-1": {
		sequence: 2,
		phase: "done",
		label: "受け入れ完了",
		startedAt: "2026-08-28T05:45:00.000Z",
		retryCount: 0,
		retryReason: null,
		nextRetryAt: null,
		live: false,
		llmActiveSeconds: 312,
		failureClass: null,
		warning: null,
		wallClock: { ...emptyWallClock(), totalSeconds: 720, llmSeconds: 312, ciSeconds: 180 },
		journey: null,
	},
};

function emptyWallClock() {
	return {
		totalSeconds: 0,
		queuedSeconds: 0,
		wakeSeconds: 0,
		prepareSeconds: 0,
		llmSeconds: 0,
		ciSeconds: 0,
		integrateSeconds: 0,
		deploySeconds: 0,
		evidenceWaitSeconds: 0,
		acceptSeconds: 0,
	};
}
